import React from 'react';
import { Download, Dna, FileText, Leaf } from 'lucide-react';

const btnClass =
    "inline-flex items-center justify-center gap-2 rounded-lg bg-moss-800 px-4 py-2 text-sm font-medium text-paper shadow-sm transition-all hover:-translate-y-0.5 hover:bg-moss-900 hover:shadow-soft";

const Downloads = () => {
    const species = [
        { 
            name: "Tomato",
            scientificName: "Solanum lycopersicum",
            genome: "/downloads/ITAG4_dapfit_scffree.fasta",
            gff: "/downloads/4.0_4.2_merge_on_ITAG4_aegis_fcounts_dapfit.gff3",
            gmt: "/downloads/4.0_4.2_merge_on_ITAG4.gmt"
        },
        { 
            name: "Mulberry",
            scientificName: "Morus alba",
            genome: "/downloads/Mal_HE_154X.fasta",
            gff: "/downloads/Mal_HE_154X_1_on_Mal_HE_154X_aegis_fcounts.gff3",
            gmt: "/downloads/Mal_HE_154X_1_on_Mal_HE_154X.gmt"
        },
        { 
            name: "Grapevine",
            scientificName: "Vitis vinifera",
            genome: "/downloads/T2T_ref_dapfit_scffree.fasta",
            gff: "/downloads/5.1_on_T2T_ref_aegis_fcounts_dapfit.gff3",
            gmt: "/downloads/5.1_on_T2T_ref.gmt"
        },
        { 
            name: "Arabidopsis",
            scientificName: "Arabidopsis thaliana",
            genome: "/downloads/TAIR10_scffree.fasta",
            gff: "/downloads/Araport11_on_TAIR10_aegis_fcounts.gff3",
            gmt: "/downloads/Araport11_on_TAIR10.gmt"
        },
        { 
            name: "Cannabis",
            scientificName: "Cannabis sativa",
            cultivars: [
                {
                    name: "CBDRx",
                    genome: "/downloads/CBDRx.fasta",
                    gff: "/downloads/CBDRx_on_CBDRx_aegis_fcounts.gff3",
                    gmt: "/downloads/CBDRx_on_CBDRx.gmt"
                },
                {
                    name: "Purple Kush",
                    genome: "/downloads/PurpleKush.fasta",
                    gff: "/downloads/PurpleKush_on_PurpleKush_aegis_fcounts.gff3",
                    gmt: "/downloads/PurpleKush_on_PurpleKush.gmt"
                },
                {
                    name: "Jamaican Lion",
                    genome: "/downloads/JamaicanLion.fasta",
                    gff: "/downloads/JamaicanLion_on_JamaicanLion_aegis_fcounts.gff3",
                    gmt: "/downloads/JamaicanLion_on_JamaicanLion.gmt"
                }
            ]
        },
        { 
            name: "Chlamydomonas",
            scientificName: "Chlamydomonas reinhardtii",
            genome: "/downloads/Chlre_6.0.fasta",
            gff: "/downloads/Chlre_6.1_on_Chlre_6.0_aegis_fcounts.gff3",
            gmt: "/downloads/Chlre_6.1_on_Chlre_6.0.gmt"
        },
        { 
            name: "Prunus",
            scientificName: "Prunus persica",
            genome: "/downloads/persica_organellefree.fasta",
            gff: "/downloads/persica_on_persica_aegis_fcounts.gff3",
            gmt: "/downloads/persica_on_persica.gmt"
        },
        { 
            name: "Strawberry",
            scientificName: "Fragaria vesca",
            genome: "/downloads/vesca_organellefree.fasta",
            gff: "/downloads/vesca_on_vesca_aegis_fcounts.gff3",
            gmt: "/downloads/vesca_on_vesca.gmt"
        },
    ];

    return (
        <div className="">
            <div className="max-w-7xl mx-auto">
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-hidden rounded-2xl border border-moss-200/70 bg-white shadow-soft">
                    <div className="bg-gradient-to-r from-moss-900 to-moss-700 p-6 text-paper">
                        <h2 className="flex items-center gap-2 font-display text-2xl font-semibold">
                            <Dna className="h-6 w-6" />
                            Available Species
                        </h2>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="border-b border-moss-200 bg-moss-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.12em] text-moss-700">
                                        Species
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.12em] text-moss-700">
                                        Scientific Name
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.12em] text-moss-700">
                                        Genome (FASTA)
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.12em] text-moss-700">
                                        Annotation (GFF)
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.12em] text-moss-700">
                                        Ontology (GMT)
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-moss-200/70">
                                {species.map((item, index) => (
                                    <tr 
                                        key={item.name} 
                                        className={`transition-colors hover:bg-moss-50 ${
                                            index % 2 === 0 ? 'bg-white' : 'bg-paper'
                                        }`}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <span className="font-display font-semibold text-ink">{item.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="italic text-ink/60">{item.scientificName}</span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {item.cultivars ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    {item.cultivars.map(cultivar => (
                                                        <a
                                                            key={cultivar.name}
                                                            href={cultivar.genome}
                                                            download
                                                            className={`w-full max-w-xs ${btnClass}`}
                                                        >
                                                            <Dna className="h-4 w-4" />
                                                            {cultivar.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            ) : (
                                                <a href={item.genome} download className={btnClass}>
                                                    <Dna className="h-4 w-4" />
                                                    Download
                                                </a>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {item.cultivars ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    {item.cultivars.map(cultivar => (
                                                        <a
                                                            key={cultivar.name}
                                                            href={cultivar.gff}
                                                            download
                                                            className={`w-full max-w-xs ${btnClass}`}
                                                        >
                                                            <FileText className="h-4 w-4" />
                                                            {cultivar.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            ) : (
                                                <a href={item.gff} download className={btnClass}>
                                                    <FileText className="h-4 w-4" />
                                                    Download
                                                </a>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {item.cultivars ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    {item.cultivars.map(cultivar => (
                                                        <a
                                                            key={cultivar.name}
                                                            href={cultivar.gmt}
                                                            download
                                                            className={`w-full max-w-xs ${btnClass}`}
                                                        >
                                                            <Leaf className="h-4 w-4" />
                                                            {cultivar.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            ) : (
                                                <a href={item.gmt} download className={btnClass}>
                                                    <Leaf className="h-4 w-4" />
                                                    Download
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Mobile Card View */}
                <div className="space-y-4 md:hidden">
                    <div className="rounded-2xl bg-gradient-to-r from-moss-900 to-moss-700 p-4 text-paper">
                        <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
                            <Dna className="h-5 w-5" />
                            Available Species
                        </h2>
                    </div>
                    
                    {species.map((item) => (
                        <div 
                            key={item.name} 
                            className="overflow-hidden rounded-2xl border border-moss-200/70 bg-white shadow-soft transition-shadow hover:shadow-lift"
                        >
                            <div className="border-b border-moss-200 bg-moss-50 p-4">
                                <div className="flex items-center gap-3">
                                    <div>
                                        <h3 className="font-display text-lg font-bold text-ink">{item.name}</h3>
                                        <p className="text-sm italic text-ink/60">{item.scientificName}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="space-y-3 p-4">
                                {item.cultivars ? (
                                    <>
                                        <div className="space-y-2">
                                            <h4 className="text-center text-sm font-semibold uppercase tracking-wider text-moss-700">Genome (FASTA)</h4>
                                            {item.cultivars.map(cultivar => (
                                                <a key={cultivar.name} href={cultivar.genome} download className={`w-full ${btnClass}`}>
                                                    <Dna className="h-4 w-4" />
                                                    Download {cultivar.name}
                                                </a>
                                            ))}
                                        </div>
                                        <div className="space-y-2 border-t border-moss-200 pt-3">
                                            <h4 className="text-center text-sm font-semibold uppercase tracking-wider text-moss-700">Annotation (GFF)</h4>
                                            {item.cultivars.map(cultivar => (
                                                <a key={cultivar.name} href={cultivar.gff} download className={`w-full ${btnClass}`}>
                                                    <FileText className="h-4 w-4" />
                                                    Download {cultivar.name}
                                                </a>
                                            ))}
                                        </div>
                                        <div className="space-y-2 border-t border-moss-200 pt-3">
                                            <h4 className="text-center text-sm font-semibold uppercase tracking-wider text-moss-700">Ontology (GMT)</h4>
                                            {item.cultivars.map(cultivar => (
                                                <a key={cultivar.name} href={cultivar.gmt} download className={`w-full ${btnClass}`}>
                                                    <Leaf className="h-4 w-4" />
                                                    Download {cultivar.name}
                                                </a>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <a href={item.genome} download className={`w-full ${btnClass}`}>
                                            <Dna className="h-4 w-4" />
                                            Download Genome (FASTA)
                                        </a>
                                        <a href={item.gff} download className={`w-full ${btnClass}`}>
                                            <FileText className="h-4 w-4" />
                                            Download Annotation (GFF)
                                        </a>
                                        <a href={item.gmt} download className={`w-full ${btnClass}`}>
                                            <Leaf className="h-4 w-4" />
                                            Download Ontology (GMT)
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer Information */}
                <div className="mt-12 rounded-2xl border border-moss-200/70 bg-moss-50 p-6 shadow-soft">
                    <div className="flex items-start gap-3">
                        <Download className="mt-1 h-5 w-5 shrink-0 text-moss-700" />
                        <div className="space-y-2 text-sm text-ink/70">
                            <p className="font-display font-semibold text-ink">File Format Information:</p>
                            <ul className="space-y-1">
                                <li>• <strong className="text-ink">FASTA files:</strong> Contain nucleotide sequences of complete genomes.</li>
                                <li>• <strong className="text-ink">GFF files:</strong> Include gene annotations and structural information.</li>
                                <li>• <strong className="text-ink">GMT files:</strong> Contain gene sets for ontology terms (Gene Matrix Transposed).</li>
                            </ul>
                            <p className="mt-3 text-xs text-ink/50">
                                Right-click and select "Save Link As" if download doesn't start automatically.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Downloads;
